import { toNumberFormatNotationShort }      from '@/lib/utils/helper'
import BaseResource                         from '@/lib/data/resource/BaseResource'
import AssetModel                           from '@/api/models/asset/AssetModel'

export default class ALMDepositsResource extends BaseResource {
  constructor (payload = {}) {
    super(payload)

    this.TitleValue = payload.deposits?.descriptiveStatistics?.value ?? ''
    this.TitleValueChange = payload.deposits?.dynamicEvolution?.totalValueChange ?? ''
    this.BaseCurrency = new AssetModel(payload.baseCurrency ?? null)

    // Deposits data
    this.Deposits = payload.deposits ?? {}
    this.DescriptiveStatistics = this.Deposits.descriptiveStatistics ?? {}
    this.DepositsMaturity = this.Deposits.depositsMaturity ?? {}
    this.InterestExpense = this.Deposits.interestExpense ?? {}
    this.TypeAllocation = this.Deposits.typeAllocation ?? {}
    this.DynamicEvolution = this.Deposits.dynamicEvolution ?? {}
    this.DepositsConcentration = this.Deposits.depositsConcentration ?? {}
    this.FixedFloatingComparison = this.Deposits.fixedFloatingComparison ?? {}
  }

  /* PROPERTIES */
  get BaseCurrencyTitle () {
    return this.baseCurrencySymbolSingle() ? this.getBaseCurrencySymbolSingle() : this.getBaseCurrencySymbolSingle() + ' '
  }

  get BaseCurrencyDisplayName () {
    return this.BaseCurrency?.displayName || ''
  }

  get BaseCurrencyDisplaySymbol () {
    return this.BaseCurrency?.displaySymbol || ''
  }

  get TitleValueFmt () {
    return this.TitleValue ? toNumberFormatNotationShort(this.TitleValue) : ''
  }

  get TitleValueChangeFmt () {
    return this.TitleValueChange ? this.BaseCurrencyTitle + toNumberFormatNotationShort(this.TitleValueChange) : ''
  }

  // Descriptive Statistics
  get DepositsSize () {
    return this.DescriptiveStatistics.size || 0
  }

  get DepositsCount () {
    return this.DescriptiveStatistics.loans || 0
  }

  get DepositsMaturityValue () {
    return this.DescriptiveStatistics.maturity || 0
  }

  get DepositsMaturityUnit () {
    return this.DescriptiveStatistics.maturityUnit || ''
  }

  get DepositsRepricingValue () {
    return this.DescriptiveStatistics.repricing || 0
  }

  get DepositsRepricingUnit () {
    return this.DescriptiveStatistics.repricingUnit || ''
  }

  get DepositsDuration () {
    return this.DescriptiveStatistics.duration || 0
  }

  get DepositsDurationUnit () {
    return this.DescriptiveStatistics.durationUnit || ''
  }

  // Interest Expense
  get InterestExpense3m () {
    return this.InterestExpense.interestExpense3m || 0
  }

  get InterestExpense12m () {
    return this.InterestExpense.interestExpense12m || 0
  }

  // Type Allocation
  get TypeNames () {
    return this.TypeAllocation.typeNames || []
  }

  get PerTypeAllocationData () {
    return this.TypeAllocation.perTypeAllocationData || []
  }

  get TotalAllocationData () {
    return this.TypeAllocation.totalAllocationData || []
  }

  // Dynamic Evolution
  get EvolutionDates () {
    return this.DynamicEvolution.dates || []
  }

  get EvolutionTypeNames () {
    return this.DynamicEvolution.typeNames || []
  }

  get EvolutionTotalValue () {
    return this.DynamicEvolution.totalValue || 0
  }

  get EvolutionTotalValueChange () {
    return this.DynamicEvolution.totalValueChange || 0
  }

  get EvolutionAmountOfContracts () {
    return this.DynamicEvolution.amountOfContracts || {}
  }

  get EvolutionAmountOfContractsChange () {
    return this.DynamicEvolution.amountOfContractsChange || 0
  }

  get EvolutionDuration () {
    return this.DynamicEvolution.duration || 0
  }

  get EvolutionDurationChange () {
    return this.DynamicEvolution.durationChange || 0
  }

  get EvolutionInterestExpense () {
    return this.DynamicEvolution.interestExpense || 0
  }

  get EvolutionInterestExpenseChange () {
    return this.DynamicEvolution.interestExpenseChange || 0
  }

  // Deposits Maturity
  get MaturityLabels () {
    return this.DepositsMaturity.labels || []
  }

  get MaturityData () {
    return this.DepositsMaturity.maturityData || []
  }

  // Deposits Concentration
  get ConcentrationTypeNames () {
    return this.DepositsConcentration.typeNames || []
  }

  get PerTypeConcentrationData () {
    return this.DepositsConcentration.perTypeConcentrationData || []
  }

  // Fixed Floating Comparison
  get FixedFloatingLabels () {
    return this.FixedFloatingComparison.labels || []
  }

  get PerLabelFixedFloatingComparisonData () {
    return this.FixedFloatingComparison.perLabelFixedFloatingComparisonData || []
  }

  /* METHODS */
  baseCurrencySymbolSingle () {
    return this.BaseCurrencyDisplaySymbol.length === 1
  }

  getBaseCurrencySymbolSingle () {
    return this.baseCurrencySymbolSingle() ? this.BaseCurrencyDisplaySymbol : this.BaseCurrencyDisplayName
  }

  // Helper method to get type allocation by type name
  getTypeAllocationByType (typeName) {
    return this.PerTypeAllocationData.find(item => item.typeName === typeName) || null
  }

  // Helper method to get concentration data by type name
  getConcentrationDataByType (typeName) {
    return this.PerTypeConcentrationData.find(item => item.typeName === typeName) || null
  }

  // Helper method to get fixed floating comparison data by label
  getFixedFloatingComparisonByLabel (label) {
    return this.PerLabelFixedFloatingComparisonData.find(item => item.label === label) || null
  }
}
